// FT-191: состояние общего компьютера. `device` — блок `device` ответа app_version (есть
// только на зарегистрированном компьютере при включённой функции); `deviceMode` — сессия
// выдана в режиме устройства (`trusted_device` в ответе входа); `locked` — экран блокировки;
// `tabClosed` — вкладку закрыла смена сотрудника в другой вкладке.
//
// Модуль персистится вместе с остальным vuex: F5 на заблокированном экране оставляет его
// заблокированным. Мутации модуля НЕ входят в createMutationsSharer — вкладки синхронизирует
// явный протокол deviceTabs.
import axios from "axios";
import { emitDeviceEvent } from "@/utils/deviceTabs";
import { deviceFromAppVersion, reloadIfBootedLocked, resetToSignin, shouldDropNonDeviceSession, switchToAnotherUser, writeIdleMinutes } from "@/utils/deviceLock";
import { APP_VERSION_PATH } from "@/utils/antiScrapeRetry";
import { markIntentionalLogout } from "@/utils/sessionExpiry";

export default {
  namespaced: true,
  state: () => ({
    device: null,
    deviceMode: false,
    locked: false,
    lockedUserId: null,
    tabClosed: false,
    switchReady: false,
    pinSetupRequested: false,
  }),
  mutations: {
    SET_DEVICE(state, device) {
      state.device = device || null;
      if (state.device) writeIdleMinutes(state.device.idle_minutes);
      if (!state.device) {
        state.deviceMode = false;
        state.locked = false;
        state.lockedUserId = null;
      }
    },
    ADD_DEVICE_USER(state, user) {
      if (!state.device) return;
      const users = state.device.users || [];
      if (users.some((u) => u.id === user.id)) return;
      state.device = { ...state.device, users: users.concat([ user ]) };
    },
    SET_DEVICE_MODE(state, value) {
      state.deviceMode = value === true;
    },
    LOCK(state, { userId } = {}) {
      state.locked = true;
      // null — явно «никто» (вход по PIN с формы входа); undefined — оставить прежнего.
      state.lockedUserId = userId === undefined ? state.lockedUserId : userId;
    },
    UNLOCK(state) {
      state.locked = false;
    },
    CLOSE_TAB(state) {
      state.tabClosed = true;
      state.switchReady = false;
    },
    SWITCH_READY(state) {
      state.switchReady = true;
    },
    // После полного входа на общем компьютере без PIN — диалог «Задайте PIN» (уровня App).
    REQUEST_PIN_SETUP(state) {
      state.pinSetupRequested = true;
    },
    CLEAR_PIN_SETUP(state) {
      state.pinSetupRequested = false;
    },
  },
  actions: {
    // Экран блокировки и стёртый токен в этой вкладке — без запроса и без рассылки (401, событие
    // другой вкладки, начало lock).
    lockLocal({ commit }, { userId } = {}) {
      commit("LOCK", { userId });
      commit("CLEAR_TOKEN_LOCAL", null, { root: true });
    },
    // Блок device ответа app_version (CTR-02). Потеря устройства в режиме устройства — полный
    // выход (FM-04), а не снятие блокировки над данными прежнего сотрудника; сессия, открытая на
    // зарегистрированном компьютере не в режиме устройства, завершается (REQ-06).
    async applyDevice({ state, commit, dispatch, rootState, rootGetters }, device) {
      if (device === undefined) return; // авария app_version — не «устройства нет»
      if (!device && state.deviceMode) {
        resetToSignin();
        return;
      }
      commit("SET_DEVICE", device);
      const user = rootState.currentUser || {};
      const mainCompany = rootGetters.getMainCompany || {};
      if (shouldDropNonDeviceSession({
        device: state.device, loggedIn: rootGetters.isLoggedIn, deviceMode: state.deviceMode,
        role: user.role, mainCompanyId: mainCompany.id,
      })) {
        await dispatch("signOutQuietly");
      }
    },
    // Тихий выход (без тоста «сессия истекла»): сессия на этом компьютере не в режиме устройства
    // или компьютер только что стал общим — следующий вход уже в режиме устройства.
    async signOutQuietly({ commit, rootGetters }) {
      try {
        await axios(markIntentionalLogout({
          url: "users/sign_out", baseURL: rootGetters.getApiUrl, method: "delete",
          headers: { Authorization: rootGetters.getAuthToken },
        }));
      } catch (e) {
        // выход локально состоится в любом случае
      }
      commit("unsetCurrentUser", null, { root: true });
    },
    // Разблокировка по PIN (CTR-03). Тот же сотрудник — новый токен на месте, страница остаётся
    // (REQ-08); другой — полная смена через перезагрузку (REQ-09). Ошибка — с кодом сервера.
    async unlock({ state, commit, rootGetters }, { userId, pin }) {
      let response;
      try {
        response = await axios.post(`${rootGetters.getApiUrl}/api/v1/trusted_device/unlock`, { user_id: userId, pin });
      } catch (error) {
        const data = (error && error.response && error.response.data) || {};
        const err = new Error("unlock failed");
        err.code = (data.error && data.error.code) || "network";
        err.lockedUntil = data.error && data.error.locked_until;
        throw err;
      }
      if (userId !== state.lockedUserId) {
        switchToAnotherUser(response);
        return { same: false };
      }
      // deviceLockPlugin: UNLOCK и свежая метка активности; deviceUnlock — не рассылать смену сессии.
      commit("setUserInfo", { ...response, deviceUnlock: true }, { root: true });
      emitDeviceEvent("unlock", { userId });
      reloadIfBootedLocked();
      return { same: true };
    },
    // Перечитать устройство (после регистрации компьютера общим).
    async refreshDevice({ dispatch, rootGetters }) {
      const response = await axios.get(`${rootGetters.getApiUrl}${APP_VERSION_PATH}`);
      await dispatch("applyDevice", deviceFromAppVersion(response.data));
    },
    // Блокировка (неактивность, кнопка, горячая клавиша). Экран и токен — сразу, отзыв на сервере —
    // следом сохранённым токеном: зависшая сеть не оставляет интерфейс открытым. Отзыв не удался —
    // сессия всё равно закончится по просрочке или при следующем входе на устройстве (REQ-06).
    lock({ dispatch, rootState, rootGetters }) {
      const userId = rootState.currentUser && rootState.currentUser.id;
      const token = rootGetters.getAuthToken || rootState.auth_token;
      dispatch("lockLocal", { userId });
      emitDeviceEvent("lock", { userId });
      if (token) {
        axios.post(`${rootGetters.getApiUrl}/api/v1/trusted_device/lock`, null, { headers: { Authorization: token } })
          .catch(() => {});
      }
    },
  },
  getters: {
    device: (state) => state.device,
    deviceMode: (state) => state.deviceMode,
    locked: (state) => state.locked,
    lockedUserId: (state) => state.lockedUserId,
    tabClosed: (state) => state.tabClosed,
    switchReady: (state) => state.switchReady,
    pinSetupRequested: (state) => state.pinSetupRequested,
    // Последний работавший — первым: чаще всего разблокирует он же.
    lockScreenUsers: (state) => {
      const users = (state.device && state.device.users) || [];
      const last = users.filter((u) => u.id === state.lockedUserId);
      return last.concat(users.filter((u) => u.id !== state.lockedUserId));
    },
  },
};
